import React, { useEffect, useState } from 'react';
import { SubmitHandler, useForm } from 'react-hook-form';
import { callApi } from '../../api/callApi';
import { API_ENDPOINT, API_URL } from '../../api/constant';
import { showToastNotification } from '../../utility/helper';
import InputField from '../../components/UI/Forms/InputField';
import Textarea from '../../components/UI/Forms/Textarea';
import ErrorMessage from '../../components/UI/Notifications/ErrorMessage';
import Button from '../../components/UI/Buttons/Button';
import { ICheckoutMessage } from '../../interfaces/checkoutMessage';

interface CheckoutMessageFormInterface {
    message: string;
    startDate: string;
    startTime: string;
    endDate: string;
    endTime: string;
    isActive: boolean;
}

const CheckoutMessageTab: React.FC = () => {
    const [loading, setLoading] = useState<boolean>(true);
    const [loadingButton, setLoadingButton] = useState<boolean>(false);
    const [isActive, setIsActive] = useState<boolean>(false);
    const { register, handleSubmit, setValue, formState: { errors } } = useForm<CheckoutMessageFormInterface>({ mode: 'onBlur' });

    useEffect(() => {
        fetchCheckoutMessage();
    }, []);

    const fetchCheckoutMessage = async () => {
        try {
            setLoading(true);
            const response = await callApi<ICheckoutMessage>(API_URL + API_ENDPOINT.order + 'checkout-message', { method: 'GET' });
            if (response) {
                setValue('message', response.message || '');
                setValue('startDate', response.startDate || '');
                setValue('startTime', response.startTime || '');
                setValue('endDate', response.endDate || '');
                setValue('endTime', response.endTime || '');
                setValue('isActive', response.isActive || false);
                setIsActive(response.isActive || false);
            }
        } catch (error: any) {
            console.error('Failed to fetch checkout message:', error.message);
        } finally {
            setLoading(false);
        }
    };

    const onSubmit: SubmitHandler<CheckoutMessageFormInterface> = async (data) => {
        setLoadingButton(true);
        try {
            const submitData = {
                ...data,
                isActive: isActive
            };

            await callApi(API_URL + API_ENDPOINT.order + 'checkout-message', { method: 'POST', data: submitData });
            showToastNotification('success', 'Checkout message updated successfully');
            fetchCheckoutMessage();
        } catch (error: any) {
            showToastNotification('danger', error.message);
        } finally {
            setLoadingButton(false);
        }
    };

    const handleIsActiveChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setIsActive(e.target.checked);
    };

    if (loading) {
        return <>Loading checkout message...</>;
    }

    return (
        <div className="row">
            <div className="col-12">
                <div className="app-card app-card-settings shadow-sm p-4">
                    <div className="app-card-body">
                        <form onSubmit={handleSubmit(onSubmit)}>
                            <div className="row">
                                {/* Message Section */}
                                <div className="mb-3 col-md-12">
                                    <Textarea
                                        label='Message'
                                        register={register('message', {
                                            required: 'Message is required'
                                        })}
                                        name="message"
                                        placeholder="Enter checkout message"
                                        rows={4}
                                    />
                                    <ErrorMessage message={errors.message?.message?.toString()} />
                                </div>

                                {/* Start Date Section */}
                                <div className="mb-3 col-md-6">
                                    <InputField
                                        label='Start Date'
                                        type="date"
                                        register={register('startDate', {
                                            required: 'Start date is required'
                                        })}
                                        name="startDate"
                                        placeholder="Select start date"
                                    />
                                    <ErrorMessage message={errors.startDate?.message?.toString()} />
                                </div>

                                {/* Start Time Section */}
                                <div className="mb-3 col-md-6">
                                    <InputField
                                        label='Start Time'
                                        type="time"
                                        register={register('startTime', {
                                            required: 'Start time is required'
                                        })}
                                        name="startTime"
                                        placeholder="Select start time"
                                    />
                                    <ErrorMessage message={errors.startTime?.message?.toString()} />
                                </div>

                                {/* End Date Section */}
                                <div className="mb-3 col-md-6">
                                    <InputField
                                        label='End Date'
                                        type="date"
                                        register={register('endDate', {
                                            required: 'End date is required'
                                        })}
                                        name="endDate"
                                        placeholder="Select end date"
                                    />
                                    <ErrorMessage message={errors.endDate?.message?.toString()} />
                                </div>

                                {/* End Time Section */}
                                <div className="mb-3 col-md-6">
                                    <InputField
                                        label='End Time'
                                        type="time"
                                        register={register('endTime', {
                                            required: 'End time is required'
                                        })}
                                        name="endTime"
                                        placeholder="Select end time"
                                    />
                                    <ErrorMessage message={errors.endTime?.message?.toString()} />
                                </div>

                                {/* Is Active Section */}
                                <div className="mb-3 col-md-6">
                                    <div className="form-check form-switch">
                                        <input
                                            className="form-check-input"
                                            type="checkbox"
                                            id="isActive"
                                            checked={isActive}
                                            onChange={handleIsActiveChange}
                                        />
                                        <label className="form-check-label" htmlFor="isActive">
                                            Active
                                        </label>
                                    </div>
                                </div>

                                {/* Submit Button Section */}
                                <div className="col-12 text-end mt-3 ">
                                    <Button
                                        loading={loadingButton}
                                        className="btn btn-primary text-white px-5"
                                        type="submit"
                                        text="Save Checkout Message"
                                    />
                                </div>
                            </div>
                        </form>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default CheckoutMessageTab;
